// ProjectTree — Worksnip's left-panel folder tree, now consuming the shared
// <TreeView> primitive (frontend/src/components/tree/). This file owns the
// data layer + business rules; <TreeView> owns the visual + interaction.
//
// What this file does:
//   • Loads /api/v1/projects/tree + /api/v1/guides in parallel
//   • Builds a normalized TreeNode[] honouring expanded state, including
//     outline display numbers ("1.1.1") and per-folder rolling badge counts
//   • Dispatches the right API call on drag-drop (project↔project,
//     guide↔project, guide↔guide), incl. cross-client warning toast
//   • Wires the row-context-menu actions (rename / new-subfolder / delete
//     / unfile) to the existing endpoints with undo toasts
//   • Pins the synthetic InboxRow + AllCapturesRow above the tree as drop
//     targets for "unfile" (set project_id NULL)
//
// What <TreeView> does (replaces ~700 lines of bespoke rendering):
//   • Hairline connector rails (V1 line-thru / line-mid / line-last)
//   • Drag-and-drop edge detection (before / after / into)
//   • Right-click context menu portaled to <body> (escapes stacking contexts)
//   • Inline new-folder + rename editors
//   • Count badges, public-id pills, display-number prefixes

import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Folder, FolderOpen, FolderPlus, Plus, Pencil, Trash2, Users, ListPlus, ListX,
  Search, X, MessageSquarePlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api } from '../../services/apiClient';
import { projectApi, guideTreeApi } from '../../services/projectApi';
import { flowsnipApi, type DiagramMeta } from '../../services/flowsnipApi';
import { useProjectTree } from '../../stores/projectTreeStore';
import { useAuth } from '../../stores/authStore';
import { useTrainingBucket } from '../../stores/trainingBucketStore';
import type { DiagramLeaf, GuideLeaf, Project, ProjectNode } from '../../types/project';
import type { Guide } from '../../types/guide';
import { AllCapturesRow, InboxRow } from './ProjectTreeNode';
import { FlowSnipIcon, PageSnipIcon, WorksnipIcon } from '../icons/SnipTypeIcons';
import { normalizeSnipKind } from '../../lib/snipKind';
import { ProjectTreeHeader } from './ProjectTreeHeader';
import { confirmChoice, confirmDialog, showUndoToast } from '../ui/ConfirmDialog';
import { TreeView, type TreeNode, type TreeMenuAction } from '../tree';
import { compareGuides, gutterNumber, splitLeadingNumber } from '../tree/numbering';

// Persistence key for the row-numbering toggle — see the state declaration
// below for why this doesn't need the shared projectTreeStore.
const NUMBERING_STORAGE_KEY = 'ws-tree-numbering';

interface ProjectTreeProps {
  /** Optional — fired when the user picks a project (or null for "All"). */
  onSelect?: (project: ProjectNode | null) => void;
  /**
   * When set, a folder click calls this INSTEAD of navigating to the folder's
   * detail page. Lets a page that is itself a folder browser (the Worksnips
   * explorer) reuse this tree as its navigation rail without being yanked to
   * another route on every click. Leaf (snip) clicks still open the snip.
   */
  onFolderSelect?: (projectId: string) => void;
  /** Companion to `onFolderSelect` for the two synthetic rows. */
  onSpecialSelect?: (which: 'all' | 'inbox') => void;
  /** Row to render as active when the consumer owns selection state. */
  activeId?: string | null;
  /**
   * Whether to render the pinned Workbench (unfiled-captures) row above the
   * tree. Defaults to true — today's behaviour for /worksnips and /projects.
   * Workbench is a guide/capture-specific concept ("fresh captures land on
   * your Workbench") with no meaning for PageSnip diagrams, so the PageSnip
   * page passes `false` here rather than the tree sniffing the URL itself.
   */
  showWorkbench?: boolean;
}

// — buildTree: same as before, kept verbatim (now also folds in PageSnip
// diagrams as a parallel leaf list — see ProjectNode.diagrams) ——
function buildTree(projects: Project[], guides: Guide[], diagrams: DiagramMeta[]): ProjectNode[] {
  const byId = new Map<string, ProjectNode>();
  projects.forEach((p) => byId.set(p.id, { ...p, children: [], guides: [], diagrams: [], depth: 0 }));
  const roots: ProjectNode[] = [];
  projects.forEach((p) => {
    const n = byId.get(p.id)!;
    if (p.parent_id && byId.has(p.parent_id)) {
      byId.get(p.parent_id)!.children.push(n);
    } else {
      roots.push(n);
    }
  });
  guides.forEach((g) => {
    if (!g.project_id) return;
    const folder = byId.get(g.project_id);
    if (!folder) return;
    folder.guides.push({
      id: g.id,
      title: g.title,
      public_id: g.public_id,
      project_id: g.project_id,
      created_at: g.created_at,
      step_count: g.step_count,
      processing_status: g.processing_status,
      depth: 0,
    });
  });
  // Unfiled diagrams (project_id null, or pointing at a folder this user's
  // fetch didn't return) simply don't render in this rail — same fallback
  // guides above take: the PageSnip page's own Inbox scope is where those
  // live, not the shared tree.
  diagrams.forEach((d) => {
    if (!d.project_id) return;
    const folder = byId.get(d.project_id);
    if (!folder) return;
    folder.diagrams.push({
      id: d.id,
      title: d.title || '',
      kind: d.kind,
      project_id: d.project_id,
      depth: 0,
    });
  });
  const sortRec = (list: ProjectNode[], depth: number) => {
    list.sort((a, b) => a.ordinal - b.ordinal);
    list.forEach((n) => {
      n.depth = depth;
      n.guides.sort(compareGuides);
      n.guides.forEach((g) => { g.depth = depth + 1; });
      // Diagrams carry no ordinal/numbered-title convention of their own;
      // compareGuides' generic {title, created_at, ordinal} shape still
      // gives a stable order (newest-first tiebreak).
      n.diagrams.sort(compareGuides);
      n.diagrams.forEach((d) => { d.depth = depth + 1; });
      sortRec(n.children, depth + 1);
    });
  };
  sortRec(roots, 0);
  return roots;
}

// — computeFolderStats: rolling per-folder badge + latest-activity ts —
function computeFolderStats(
  tree: ProjectNode[],
): Map<string, { count: number; latest?: string }> {
  const out = new Map<string, { count: number; latest?: string }>();
  const walk = (n: ProjectNode): { count: number; latest?: string } => {
    let count = n.guides.length + n.diagrams.length;
    let latest = n.guides.reduce<string | undefined>((acc, g) => {
      if (!g.created_at) return acc;
      if (!acc) return g.created_at;
      return Date.parse(g.created_at) > Date.parse(acc) ? g.created_at : acc;
    }, undefined);
    for (const child of n.children) {
      const s = walk(child);
      count += s.count;
      if (s.latest && (!latest || Date.parse(s.latest) > Date.parse(latest))) {
        latest = s.latest;
      }
    }
    out.set(n.id, { count, latest });
    return { count, latest };
  };
  tree.forEach(walk);
  return out;
}

// — flatten honouring expanded state, producing TreeNode[] for <TreeView> —
// Every row carries a 2-digit number in the tree's narrow gutter. A row whose
// stored name already begins with an ordering prefix ("01-Finance") shows THAT
// number and drops it from the label, so a workspace that bakes numbering into
// its names doesn't render "01  01-Finance". Rows without a prefix get their
// sibling position. Numbers are per-sibling-group, not a dotted outline —
// horizontal space in the rail is scarce and the indent already shows depth.
//
// `numberingOn` (the header toggle beside Expand-all) gates ONLY the gutter
// display — `gutterNumber` still runs (its `label` half also strips a baked-in
// "01-" prefix from the visible text), so turning numbering off doesn't
// un-strip a workspace's own numbered filenames back into the label; it just
// stops rendering a *computed* number next to a name that may already carry
// its own. This is the fix for the "01 · 01 Something" complaint: a name
// numbered without a recognized separator (no "-"/"."/")" after the digits)
// doesn't match the baked-in-prefix regex, so the gutter fell back to a
// sibling-position number that duplicated the name's own leading digits.
function flattenForTreeView(
  tree: ProjectNode[],
  expandedMap: Record<string, boolean>,
  folderStats: Map<string, { count: number; latest?: string }>,
  threadCounts: Record<string, number>,
  // When provided (search active), only nodes in `keep` are emitted and every
  // kept folder is force-expanded so the path to each match stays visible —
  // regardless of the user's saved expand/collapse state.
  keep: Set<string> | null | undefined,
  t: TFunction,
  numberingOn: boolean,
): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (
    list: ProjectNode[],
    parentId: string | null,
    depth: number,
  ) => {
    list.forEach((n, idx) => {
      if (keep && !keep.has(n.id)) return;  // prune non-matching branches
      const stats = folderStats.get(n.id) ?? { count: 0, latest: undefined };
      const hasChildren = n.children.length > 0 || n.guides.length > 0 || n.diagrams.length > 0;
      const { displayNumber, label } = gutterNumber(n.name, idx);
      out.push({
        id: n.id,
        parentId,
        depth,
        kind: depth === 0 ? 'project' : 'folder',
        label,
        displayNumber: numberingOn ? displayNumber : undefined,
        count: stats.count || undefined,
        hasChildren,
      });
      // Force-expand kept folders while searching; otherwise honour saved state.
      const expanded = keep ? true : !!expandedMap[n.id];
      if (expanded) {
        if (n.children.length) walk(n.children, n.id, depth + 1);
        n.guides.forEach((g: GuideLeaf, gi) => {
          if (keep && !keep.has(g.id)) return;
          const leaf = gutterNumber(g.title || t('tree.main.untitledSnip'), gi);
          out.push({
            id: g.id,
            parentId: n.id,
            depth: depth + 1,
            kind: 'leaf',
            label: leaf.label,
            displayNumber: numberingOn ? leaf.displayNumber : undefined,
            hasChildren: false,
            threadCount: threadCounts[g.id] || undefined,
          });
        });
        // PageSnip diagrams — a parallel leaf list to guides, after them.
        // Distinct 'diagram' kind so renderIcon/click/drag branch off it:
        // PageSnip Flow/Guide glyph (picked below via `snipKind`), click
        // navigates straight to /pagesnip/:id (no select-then-double-click),
        // and it's never a drag source or drop target in this shared tree
        // (the PageSnip page owns re-parenting).
        n.diagrams.forEach((d: DiagramLeaf, di) => {
          if (keep && !keep.has(d.id)) return;
          const leaf = gutterNumber(d.title || t('tree.main.untitledDiagram'), di);
          out.push({
            id: d.id,
            parentId: n.id,
            depth: depth + 1,
            kind: 'diagram',
            label: leaf.label,
            displayNumber: numberingOn ? leaf.displayNumber : undefined,
            hasChildren: false,
            noDrag: true,
            noDrop: true,
            // 'flow' | 'guide' — lets renderIcon below pick the right glyph;
            // normalized so legacy/missing rows fall back the same way (see
            // lib/snipKind.ts).
            snipKind: normalizeSnipKind(d.kind),
          });
        });
      }
    });
  };
  walk(tree, null, 0);
  return out;
}

// — search: collect matching node ids (folders + leaves), their ancestors (so
// the path stays visible), and per-node matched-character indices for <mark>
// highlighting. Substring (case-insensitive) match — fast + predictable, no
// fuzzy surprises in a folder tree.
function collectMatches(
  tree: ProjectNode[],
  rawQuery: string,
  t: TFunction,
): { keep: Set<string>; matchIdx: Record<string, number[]> } {
  const q = rawQuery.trim().toLowerCase();
  const keep = new Set<string>();
  const matchIdx: Record<string, number[]> = {};
  const parent = new Map<string, string | null>();

  const markKeep = (id: string) => {
    let cur: string | null = id;
    while (cur != null && !keep.has(cur)) {
      keep.add(cur);
      cur = parent.get(cur) ?? null;
    }
  };
  // We match against the FULL stored name — a user searching "101" must still
  // find "101 — Create Customer" even though the tree renders "101" in the
  // number gutter rather than in the label. But the highlighter indexes into
  // the *visible* (prefix-stripped) label, so shift the matched positions left
  // by however much we hid, and drop any that land inside the hidden prefix.
  const matchAgainst = (id: string, name: string) => {
    const pos = name.toLowerCase().indexOf(q);
    if (pos < 0) return;
    markKeep(id);
    const { rest } = splitLeadingNumber(name);
    const offset = rest === name ? 0 : Math.max(0, name.lastIndexOf(rest));
    const idxs: number[] = [];
    for (let i = pos; i < pos + q.length; i += 1) {
      const shifted = i - offset;
      if (shifted >= 0 && shifted < rest.length) idxs.push(shifted);
    }
    if (idxs.length) matchIdx[id] = idxs;
  };

  const walk = (list: ProjectNode[], parentId: string | null) => {
    for (const n of list) {
      parent.set(n.id, parentId);
      matchAgainst(n.id, n.name);
      for (const g of n.guides) {
        parent.set(g.id, n.id);
        matchAgainst(g.id, g.title || t('tree.main.untitledSnip'));
      }
      for (const d of n.diagrams) {
        parent.set(d.id, n.id);
        matchAgainst(d.id, d.title || t('tree.main.untitledDiagram'));
      }
      walk(n.children, n.id);
    }
  };
  walk(tree, null);
  return { keep, matchIdx };
}

export function ProjectTree({ onSelect, onFolderSelect, onSpecialSelect, activeId, showWorkbench = true }: ProjectTreeProps) {
  const { t, i18n } = useTranslation('projects');
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  // PageSnip diagrams — the shared tree's other leaf kind alongside guides.
  // Best-effort: reload() swallows a load failure (e.g. PageSnip not
  // entitled for this org) to an empty list rather than blocking the tree.
  const [diagrams, setDiagrams] = useState<DiagramMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  /** Parent id under which the inline "new folder" input is open. `null` =
   * root-level, `undefined` = no input visible. */
  const [newFolderParentId, setNewFolderParentId] = useState<string | null | undefined>(undefined);
  // BP 22/03 — per-guide open-thread counts, so snips with live conversations
  // get a 💬 badge in the tree. Best-effort: a fetch failure just hides badges.
  const [threadCounts, setThreadCounts] = useState<Record<string, number>>({});

  // — Store integration (selectedId, expandedMap) —
  const expandedMap   = useProjectTree((s) => s.expandedMap);
  const selectedId    = useProjectTree((s) => s.selectedId);
  const toggleExpanded= useProjectTree((s) => s.toggleExpanded);
  const select        = useProjectTree((s) => s.select);
  const loadStoreTree = useProjectTree((s) => s.loadTree);
  const expandAll     = useProjectTree((s) => s.expandAll);
  const collapseAll   = useProjectTree((s) => s.collapseAll);

  // — Row numbering toggle (header, beside Expand-all) — a workspace that
  // already bakes its own numbering into filenames ("01 Something") sees the
  // computed gutter number as a duplicate rather than a help; this lets them
  // turn it off. Component-local + localStorage (mirrors ProcessStudioPage's
  // `flowsnip-view`/`flowsnip-sort` persistence — no shared store needed for
  // a single boolean). Default ON so existing users see no change.
  const [numberingOn, setNumberingOn] = useState<boolean>(() => {
    try { return localStorage.getItem(NUMBERING_STORAGE_KEY) !== 'off'; } catch { return true; }
  });
  const toggleNumbering = useCallback(() => {
    setNumberingOn((cur) => {
      const next = !cur;
      try { localStorage.setItem(NUMBERING_STORAGE_KEY, next ? 'on' : 'off'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // — Training Bucket — managers/creators can drop folders + snips into a
  // persistent bucket (assigned later from the Learning Center's Bucket tab).
  const user = useAuth((s) => s.user);
  // Creator-seat access drives whether a double-clicked snip opens in Studio
  // (edit) or the read-only viewer. Mirrors AppShell's gate (default true).
  const canCreate = (user as { can_create?: boolean } | null)?.can_create !== false;
  const isManager =
    canCreate ||
    !!(user as { is_org_admin?: boolean } | null)?.is_org_admin ||
    !!(user as { is_super_admin?: boolean } | null)?.is_super_admin;
  const bucketItems   = useTrainingBucket((s) => s.items);
  const toggleBucket  = useTrainingBucket((s) => s.toggle);
  const inBucket = useCallback(
    (type: 'guide' | 'project', id: string) =>
      bucketItems.some((b) => b.type === type && b.id === id),
    [bucketItems],
  );

  // — Search/filter (top of the panel) —
  // `query` updates the input instantly (focus + caret never drop); the heavy
  // tree re-filter reads `deferredQuery` so a big tree can't make typing lag
  // (React schedules the expensive render at lower priority).
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const searching = deferredQuery.trim().length > 0;

  const tree        = useMemo(() => buildTree(projects, guides, diagrams), [projects, guides, diagrams]);
  const folderStats = useMemo(() => computeFolderStats(tree), [tree]);

  const { keep, matchIdx } = useMemo(
    () => searching
      ? collectMatches(tree, deferredQuery, t)
      : { keep: null as Set<string> | null, matchIdx: undefined as Record<string, number[]> | undefined },
    [tree, deferredQuery, searching, t],
  );

  const nodes       = useMemo(
    () => flattenForTreeView(tree, expandedMap, folderStats, threadCounts, keep, t, numberingOn),
    [tree, expandedMap, folderStats, threadCounts, keep, t, numberingOn],
  );

  // While searching, every emitted folder is force-expanded (chevrons open) so
  // the path to each match is visible; otherwise honour the saved expand state.
  const expandedIds = useMemo(
    () => searching
      ? nodes.filter(n => n.kind !== 'leaf' && n.kind !== 'diagram').map(n => n.id)
      : Object.keys(expandedMap).filter(k => expandedMap[k]),
    [searching, nodes, expandedMap],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, g, d] = await Promise.all([
        projectApi.fetchTree(),
        guideTreeApi.list(),
        // Best-effort — orgs without PageSnip entitled 402/403 here (see
        // requireProcessStudio); that must not fail the whole tree load.
        flowsnipApi.list().catch(() => [] as DiagramMeta[]),
      ]);
      setProjects(p);
      setGuides(g);
      setDiagrams(d);
      // Best-effort open-thread counts for the 💬 badges (never blocks the tree).
      api.get('/api/v1/threads/guide-counts')
        .then(r => setThreadCounts(r.data?.counts || {}))
        .catch(() => { /* badges just won't show */ });
      // Refresh the store so ProjectsPage's right pane sees fresh data.
      void loadStoreTree?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [loadStoreTree]);

  useEffect(() => { void reload(); }, [reload]);

  // BP 54 — the Projects Tree main panel mutates folders/guides through the same
  // APIs but this component owns its own fetched copies; reload when the panel
  // signals a structural change. Nonce starts at 0 and loadTree() never bumps it,
  // so there is no reload loop with loadStoreTree() (:330).
  const mutationNonce = useProjectTree((s) => s.treeMutationNonce);
  const seenNonce = useRef(0);
  useEffect(() => {
    if (mutationNonce === seenNonce.current) return;
    seenNonce.current = mutationNonce;
    void reload();
  }, [mutationNonce, reload]);

  // Mirror selection out so the Dashboard can scope its grid.
  useEffect(() => {
    if (!onSelect) return;
    if (!selectedId) { onSelect(null); return; }
    let found: ProjectNode | null = null;
    const walk = (list: ProjectNode[]) => {
      for (const n of list) {
        if (found) return;
        if (n.id === selectedId) { found = n; return; }
        if (n.children.length) walk(n.children);
      }
    };
    walk(tree);
    onSelect(found);
  }, [selectedId, tree, onSelect]);

  // — drag-drop dispatcher ────────────────────────────────────────────────
  // Maps the TreeView's normalized {srcId, dstId, edge} → the right backend
  // call. Same cases as the prior implementation: project↔project,
  // guide↔project (incl. cross-client warning), guide↔guide.
  const handleDragDrop = useCallback(
    async (drop: { srcId: string; dstId: string; edge: 'before' | 'after' | 'into' }) => {
      const srcKind: 'project' | 'guide' | null =
        projects.find(p => p.id === drop.srcId) ? 'project' :
        guides.find(g => g.id === drop.srcId) ? 'guide' : null;
      const dstKind: 'project' | 'guide' | null =
        projects.find(p => p.id === drop.dstId) ? 'project' :
        guides.find(g => g.id === drop.dstId) ? 'guide' : null;
      if (!srcKind || !dstKind) return;
      if (drop.srcId === drop.dstId) return;

      const projectById = new Map<string, ProjectNode>();
      const walk = (list: ProjectNode[]) => {
        for (const n of list) { projectById.set(n.id, n); walk(n.children); }
      };
      walk(tree);
      const guideById = new Map<string, Guide>(guides.map((g) => [g.id, g]));

      try {
        if (srcKind === 'project') {
          const dragged = projectById.get(drop.srcId);
          if (!dragged) return;
          let newParent: string | null;
          let newOrdinal: number;
          if (dstKind === 'project' && drop.edge === 'into') {
            const target = projectById.get(drop.dstId);
            if (!target || target.id === dragged.id) return;
            newParent = target.id;
            newOrdinal = target.children.length;
          } else if (dstKind === 'project') {
            const target = projectById.get(drop.dstId);
            if (!target) return;
            newParent = target.parent_id;
            const siblings = (newParent
              ? projectById.get(newParent)!.children
              : tree
            ).filter((n) => n.id !== dragged.id);
            const targetIdx = siblings.findIndex((n) => n.id === target.id);
            newOrdinal = drop.edge === 'before' ? targetIdx : targetIdx + 1;
          } else {
            // Dropped on a guide row — land in that guide's parent folder.
            const targetGuide = guideById.get(drop.dstId);
            if (!targetGuide) return;
            newParent = targetGuide.project_id || null;
            newOrdinal = 999_999;
          }
          await projectApi.move(dragged.id, newParent, newOrdinal);
          await reload();
          return;
        }
        if (srcKind === 'guide') {
          let nextProjectId: string | null;
          if (dstKind === 'project') {
            nextProjectId = drop.edge === 'into'
              ? drop.dstId
              : (projectById.get(drop.dstId)?.parent_id ?? null);
          } else {
            const targetGuide = guideById.get(drop.dstId);
            if (!targetGuide) return;
            nextProjectId = targetGuide.project_id || null;
          }
          const src = guideById.get(drop.srcId);
          const prevProjectId = src?.project_id ?? null;
          if (src && prevProjectId === nextProjectId) return;

          // Cross-client warning toast — surface when the root ancestor changes.
          const srcRoot = rootFor(prevProjectId, projectById);
          const dstRoot = rootFor(nextProjectId, projectById);

          await guideTreeApi.setProject(drop.srcId, nextProjectId);
          await reload();

          if (srcRoot && dstRoot && srcRoot.id !== dstRoot.id) {
            showUndoToast({
              message: t('tree.main.movedAcrossClients', { from: srcRoot.name, to: dstRoot.name }),
              onUndo: async () => {
                try {
                  await guideTreeApi.setProject(drop.srcId, prevProjectId);
                  await reload();
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                }
              },
            });
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [projects, guides, tree, reload, t],
  );

  // — context-menu actions ───────────────────────────────────────────────
  const getContextActions = useCallback(
    (node: TreeNode): TreeMenuAction[] => {
      // PageSnip diagrams don't get a context menu in this shared tree —
      // rename/delete/share for them live on the PageSnip page (/pagesnip),
      // which owns their lifecycle.
      if (node.kind === 'diagram') return [];

      // Manager-only "add to training bucket" toggle — folders assign as a
      // 'project', snips (leaves) as a 'guide'. Hidden for pure viewers.
      const bucketType: 'guide' | 'project' = node.kind === 'leaf' ? 'guide' : 'project';
      const isIn = isManager && inBucket(bucketType, node.id);
      const bucketAction: TreeMenuAction | null = isManager
        ? {
            id: 'bucket-toggle',
            label: isIn ? t('tree.main.removeFromBucket') : t('tree.main.addToBucket'),
            icon: isIn ? <ListX size={13} /> : <ListPlus size={13} />,
            divider: true,
          }
        : null;

      if (node.kind === 'leaf') {
        // Guide leaf actions — rename + discuss + delete (+ bucket for managers).
        return [
          { id: 'rename', label: t('tree.node.rename'), icon: <Pencil size={13} />, kbd: 'F2' },
          { id: 'discuss', label: t('tree.main.discussOnSnipBoard'), icon: <MessageSquarePlus size={13} /> },
          { id: 'delete', label: t('tree.main.deleteEllipsis'), icon: <Trash2 size={13} />, danger: true, divider: true, kbd: '⌫' },
          ...(bucketAction ? [bucketAction] : []),
        ];
      }
      return [
        { id: 'rename',  label: t('tree.node.rename'),        icon: <Pencil size={13} />,      kbd: 'F2' },
        { id: 'new-sub', label: t('tree.main.newSubfolder'), icon: <FolderPlus size={13} /> },
        { id: 'share',   label: t('tree.main.shareEllipsis'), icon: <Users size={13} /> },
        { id: 'delete',  label: t('tree.main.deleteEllipsis'), icon: <Trash2 size={13} />,      danger: true, divider: true, kbd: '⌫' },
        ...(bucketAction ? [bucketAction] : []),
      ];
    },
    [isManager, inBucket, t],
  );

  const handleContextAction = useCallback(
    async (nodeId: string, actionId: string) => {
      if (actionId === 'bucket-toggle') {
        // Resolve the node's type + label from the flattened render list.
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) return;
        const type: 'guide' | 'project' = node.kind === 'leaf' ? 'guide' : 'project';
        toggleBucket({ type, id: nodeId, label: node.label });
        return;
      }
      if (actionId === 'rename') {
        setRenamingId(nodeId);
        return;
      }
      if (actionId === 'discuss') {
        // Open the Snip Board's new-thread composer pre-targeted to this snip.
        // The target rides in router state (kept out of the URL); SnipBoardLayout
        // consumes it on arrival. See NewThreadDialog.
        const g = guides.find((x) => x.id === nodeId);
        navigate('/board', {
          state: { compose: { guideId: nodeId, guideTitle: g?.title || t('tree.main.untitledSnip') } },
        });
        return;
      }
      if (actionId === 'new-sub') {
        if (!expandedMap[nodeId]) toggleExpanded(nodeId);
        setNewFolderParentId(nodeId);
        return;
      }
      if (actionId === 'share') {
        // BP 03 — open the folder's full "People & access" page (no popup).
        navigate(`/projects/${nodeId}?tab=members`);
        return;
      }
      if (actionId === 'delete') {
        const proj = projects.find((p) => p.id === nodeId);
        if (proj) {
          // Deleting a folder archives its whole subtree, so the user has to be
          // told what's in there and decide what happens to it. Counts come
          // from the server (recursive) — the client tree only holds the
          // branches that happen to be expanded.
          let inside = { subfolders: 0, guides: 0 };
          try {
            inside = await projectApi.contents(nodeId);
          } catch {
            // Counts are advisory. If the probe fails, still let them delete —
            // but say so, and keep the non-destructive branch as the default.
          }
          const parts = [
            inside.subfolders ? t('tree.main.delete.subfolderCount', { count: inside.subfolders }) : null,
            inside.guides ? t('tree.main.delete.worksnipCount', { count: inside.guides }) : null,
          ].filter(Boolean) as string[];

          let mode: string | null = 'unfile';
          if (parts.length) {
            mode = await confirmChoice({
              title: t('tree.main.delete.title', { name: proj.name }),
              message: t('tree.main.delete.holdsMessage', {
                parts: new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(parts),
                folderClause: inside.subfolders ? t('tree.main.delete.folderClauseWithSubfolders') : t('tree.main.delete.folderClauseOnly'),
              }),
              choices: [
                {
                  id: 'unfile',
                  label: t('tree.main.delete.keepWorksnips'),
                  description: t('tree.main.delete.keepWorksnipsDesc', {
                    subject: inside.guides ? String(inside.guides) : t('tree.main.delete.theyPronoun'),
                  }),
                },
                {
                  id: 'cascade',
                  label: t('tree.main.delete.deleteWorksnipsToo'),
                  description: t('tree.main.delete.deleteWorksnipsTooDesc'),
                  danger: true,
                },
              ],
              defaultChoiceId: 'unfile',
              confirmLabel: t('tree.main.delete.confirmLabel'),
              danger: true,
            });
          } else {
            const ok = await confirmDialog({
              title: t('tree.main.delete.title', { name: proj.name }),
              message: t('tree.main.delete.emptyMessage'),
              confirmLabel: t('tree.main.delete.confirmLabel'),
              danger: true,
            });
            mode = ok ? 'unfile' : null;
          }
          if (!mode) return;
          try {
            await projectApi.remove(nodeId, mode as 'unfile' | 'cascade');
            if (selectedId === nodeId) select(null);
            await reload();
            showUndoToast({
              message: mode === 'cascade' && inside.guides
                ? t('tree.main.delete.toastWithGuides', { name: proj.name, count: inside.guides })
                : t('tree.main.delete.toastSimple', { name: proj.name }),
            });
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
          return;
        }
        const g = guides.find((x) => x.id === nodeId);
        if (g) {
          // Guide delete (soft delete via status='archived' for undo).
          const ok = await confirmDialog({
            title: t('tree.main.delete.title', { name: g.title || t('tree.main.delete.thisCaptureFallback') }),
            message: t('tree.main.delete.guideMessage'),
            confirmLabel: t('common:actions.delete'),
            danger: true,
          });
          if (!ok) return;
          const previousStatus = g.status;
          try {
            await api.patch(`/api/v1/guides/${nodeId}`, { status: 'archived' });
            await reload();
            showUndoToast({
              message: t('tree.main.delete.toastSimple', { name: g.title || t('tree.main.delete.captureFallback') }),
              onUndo: async () => {
                await api.patch(`/api/v1/guides/${nodeId}`, { status: previousStatus });
                await reload();
              },
            });
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
        }
      }
    },
    [projects, guides, selectedId, select, reload, expandedMap, toggleExpanded, nodes, toggleBucket, navigate, t],
  );

  // — inline submit handlers (rename + new-folder) ───────────────────────
  const handleSubmitRename = useCallback(
    async (id: string, next: string) => {
      setRenamingId(null);
      try {
        if (projects.find(p => p.id === id)) {
          await projectApi.rename(id, next);
        } else if (guides.find(g => g.id === id)) {
          await guideTreeApi.rename(id, next);
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [projects, guides, reload],
  );

  // A plain "New folder" / "+" nests under the currently-focused folder (the consumer's
  // `activeId`, else the store `selectedId`); a focused snip nests under its folder; nothing
  // focused → root. Previously all three of these buttons hard-passed null, so a subfolder
  // made while a folder was focused landed as a sibling at root. Shared across every tree.
  const resolveNewFolderParent = useCallback((): string | null => {
    const candidate = activeId ?? selectedId;
    if (!candidate || candidate === '__all' || candidate === '__inbox') return null;
    if (projects.some((p) => p.id === candidate)) return candidate;   // a folder → nest inside it
    const g = guides.find((x) => x.id === candidate);                 // a snip → nest in its folder
    return g ? (g.project_id ?? null) : null;
  }, [activeId, selectedId, projects, guides]);

  const handleSubmitNewFolder = useCallback(
    async (parentId: string | null, name: string) => {
      setNewFolderParentId(undefined);
      try {
        const created = await projectApi.create(name, parentId);
        if (parentId && !expandedMap[parentId]) toggleExpanded(parentId);
        await reload();
        select(created.id);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [reload, expandedMap, toggleExpanded, select],
  );

  // — node click — single-click opens the detail panel; DOUBLE-click a snip
  // opens it (Studio if the user has creator access, else the read-only Flick
  // viewer). We disambiguate with a short timer: the single-click action is
  // deferred ~250ms so a second click can cancel it and open instead. —
  const clickRef = useRef<{ id: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const handleNodeSelect = useCallback(
    (id: string) => {
      // PageSnip diagram — a plain click navigates straight into the
      // PageSnip viewer (never Studio, never a select-then-double-click like
      // guides get). No pending-click disambiguation needed since there's no
      // second action a double-click would unlock here.
      const dg = diagrams.find(x => x.id === id);
      if (dg) {
        navigate(`/pagesnip/${dg.id}`);
        return;
      }
      const g = guides.find(x => x.id === id);
      if (g) {
        // Second click on the same snip within the window → open it.
        if (clickRef.current && clickRef.current.id === id) {
          clearTimeout(clickRef.current.timer);
          clickRef.current = null;
          navigate(canCreate ? `/guides/${g.id}` : `/guides/${g.id}/flick`);
          return;
        }
        // First click → defer the detail-panel nav so a double-click can cancel it.
        if (clickRef.current) clearTimeout(clickRef.current.timer);
        const timer = setTimeout(() => {
          clickRef.current = null;
          // BP 16 — a single file click opens its detail (Overview + People &
          // access tabs); "Open in Studio" there also loads the editor.
          select(id);
          navigate(`/guides/${g.id}/access`);
        }, 250);
        clickRef.current = { id, timer };
        return;
      }
      const p = projects.find(x => x.id === id);
      if (p) {
        select(id);
        // A host page that IS a folder browser handles the click itself; the
        // default is BP 16's tabbed folder detail (Worksnips / People & access
        // / Settings), not the bare captures grid.
        if (onFolderSelect) onFolderSelect(id);
        else navigate(`/projects/${id}`);
      }
    },
    [diagrams, guides, projects, navigate, select, canCreate, onFolderSelect],
  );

  // — icon resolver — folder / folder-open / Worksnip film / PageSnip glyph —
  // Colors come from snip-type-icons.css's semantic classes (imported via
  // SnipTypeIcons.tsx), not a background chip: outline/stroke only, so the
  // glyph reads as a type indicator at a glance without competing with the
  // row's selection/hover fill.
  const renderIcon = useCallback(
    (n: TreeNode) => {
      if (n.kind === 'leaf') return <WorksnipIcon size={14} className="ws-worksnip-ink" />;
      // PageSnip diagrams get a distinct glyph per `snipKind` (already
      // normalized to 'flow' | 'guide') so Flow vs Guide still reads at a
      // glance — both share the one PageSnip identity color.
      if (n.kind === 'diagram') {
        return n.snipKind === 'guide'
          ? <PageSnipIcon size={14} className="ws-pagesnip-ink" />
          : <FlowSnipIcon size={14} className="ws-pagesnip-ink" />;
      }
      const isOpen = !!expandedMap[n.id];
      return isOpen ? <FolderOpen size={14} /> : <Folder size={14} />;
    },
    [expandedMap],
  );

  // Expand-all targets only rows that can actually open — a folder with no
  // children and no snips would otherwise count as "not expanded" forever and
  // pin the toggle to "Expand all".
  const expandableIds = useMemo(() => {
    const withKids = new Set<string>();
    for (const p of projects) if (p.parent_id) withKids.add(p.parent_id);
    for (const g of guides) if (g.project_id) withKids.add(g.project_id);
    for (const d of diagrams) if (d.project_id) withKids.add(d.project_id);
    return [...withKids];
  }, [projects, guides, diagrams]);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expandedMap[id]);
  const handleToggleExpandAll = useCallback(() => {
    if (allExpanded) collapseAll();
    else expandAll(expandableIds);
  }, [allExpanded, expandableIds, collapseAll, expandAll]);

  // When the host owns selection (`activeId`), highlight from that; otherwise
  // fall back to the shared store so cross-page jumps keep their context.
  const effectiveSelectedId = activeId !== undefined ? activeId : selectedId;
  const isAllActive   = effectiveSelectedId === null;
  const isInboxActive = effectiveSelectedId === '__inbox';
  // "All items" now serves both /worksnips and /pagesnip from one tree. On the
  // type-neutral consumer (`showWorkbench=false`, i.e. /pagesnip) its badge
  // counts every leaf kind the tree holds — guides + PageSnip diagrams —
  // otherwise it would silently undercount there. On the guide-centric pages
  // (/worksnips, /projects — `showWorkbench` at its true default) it stays
  // guide-only, matching today's count exactly; this relabel isn't meant to
  // also change what those pages' badge shows.
  const totalItems    = showWorkbench ? guides.length : guides.length + diagrams.length;
  // Workbench stays guide-only — it's the unfiled-CAPTURES concept, which has
  // no PageSnip equivalent (see `showWorkbench` above).
  const inboxCount    = guides.filter((g) => !g.project_id).length;

  // The rename editor must prefill with the STORED name, not the tree's
  // prefix-stripped label — otherwise confirming a rename silently deletes the
  // row's "01-" ordering prefix.
  const inlineEditValue = renamingId
    ? (projects.find((p) => p.id === renamingId)?.name
       ?? guides.find((g) => g.id === renamingId)?.title)
    : undefined;

  return (
    <div className="h-full flex flex-col bg-ws-surface border-r border-ws-border">
      <ProjectTreeHeader
        label={t('tree.header.defaultLabel')}
        onAdd={() => setNewFolderParentId(resolveNewFolderParent())}
        onRefresh={() => void reload()}
        onToggleExpandAll={handleToggleExpandAll}
        allExpanded={allExpanded}
        onToggleNumbering={toggleNumbering}
        numberingOn={numberingOn}
      />

      {/* — Quick search/filter — sits above the Workbench row. Filters the tree
          to matching folders + snips (and their parents) and highlights the
          match. Debounced via useDeferredValue so typing never lags. — */}
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-ws-ink-3 pointer-events-none"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
            placeholder={t('tree.main.filterPlaceholder')}
            aria-label={t('tree.main.filterAriaLabel')}
            // Mobile (< lg): taller field + 16px text — iOS Safari auto-zooms
            // any focused input below 16px, which wrecks the drawer layout.
            className="ws-input w-full !h-8 !pl-7 !pr-7 !text-xs max-lg:!h-10 max-lg:!pr-9 max-lg:!text-base"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label={t('tree.main.clearFilterAriaLabel')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 max-lg:w-8 max-lg:h-8 rounded-full text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div
        aria-label={t('tree.main.projectTreeAriaLabel')}
        className="flex-1 overflow-y-auto"
      >
        {/* — Synthetic InboxRow + AllCapturesRow (drop targets for unfile).
            Hidden while searching — they aren't part of the filterable tree. — */}
        {!searching && (
        <>
        {showWorkbench && (
        <InboxRow
          count={inboxCount}
          isActive={isInboxActive}
          onSelect={() => {
            select('__inbox');
            if (onSpecialSelect) onSpecialSelect('inbox');
            else navigate('/folders/__inbox');
          }}
          onDragOver={(e) => {
            // Allow guide drops onto the inbox row (unfile).
            // (TreeView's drag handles use dataTransfer; this row is outside it.)
            if (e.dataTransfer.types.includes('text/plain')) {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }
          }}
          onDrop={async (e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData('text/plain');
            if (!id) return;
            const g = guides.find(x => x.id === id);
            if (!g) return;
            try {
              await guideTreeApi.setProject(id, null);
              await reload();
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
        />
        )}
        <AllCapturesRow
          total={totalItems}
          isActive={isAllActive}
          onSelect={() => {
            select(null);
            if (onSpecialSelect) onSpecialSelect('all');
            else navigate('/captures');
          }}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('text/plain')) {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }
          }}
          onDrop={async (e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData('text/plain');
            if (!id) return;
            const g = guides.find(x => x.id === id);
            if (!g) return;
            try {
              await guideTreeApi.setProject(id, null);
              await reload();
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
        />

        <div className="mx-3 h-px bg-ws-border my-1" aria-hidden />
        </>
        )}

        {loading && <SkeletonRows count={6} />}
        {!loading && error && (
          <div className="px-3 py-2 text-xs text-ws-danger">
            {t('tree.main.failedToLoad', { error })}
          </div>
        )}
        {!loading && !error && !searching && nodes.length === 0 && newFolderParentId === undefined && (
          <EmptyState onCreate={() => setNewFolderParentId(resolveNewFolderParent())} />
        )}
        {!loading && !error && searching && nodes.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-ws-ink-3">
            {t('tree.main.noMatchesBefore')} <span className="font-medium text-ws-ink-2">"{deferredQuery.trim()}"</span>{t('tree.main.noMatchesAfter')}
          </div>
        )}

        {!loading && !error && (
          <TreeView
            nodes={nodes}
            expandedIds={expandedIds}
            searchMatchIdx={matchIdx}
            selectedId={effectiveSelectedId ?? undefined}
            editMode  /* AppShell always renders the tree in editable mode */
            inlineEditId={renamingId}
            inlineEditValue={inlineEditValue}
            // Tri-state: undefined = no editor, null = root-level editor,
            // string = editor under that parent. See SnipFolders for the
            // bug history — the old `=== undefined ? null` coercion
            // always rendered the root editor.
            inlineNewFolderParentId={newFolderParentId}
            onSelect={handleNodeSelect}
            onThreadClick={(id) => navigate(`/guides/${id}#threads`)}
            onToggleExpand={toggleExpanded}
            onDragDrop={handleDragDrop}
            onSubmitRename={handleSubmitRename}
            onCancelRename={() => setRenamingId(null)}
            onSubmitNewFolder={handleSubmitNewFolder}
            onCancelNewFolder={() => setNewFolderParentId(undefined)}
            getContextActions={getContextActions}
            onContextAction={handleContextAction}
            renderIcon={renderIcon}
            ariaLabel={t('tree.main.projectTreeAriaLabel')}
          />
        )}
      </div>

      {/* Sticky bottom — quick "new top-level folder" action. */}
      <div className="border-t border-ws-border flex-shrink-0 bg-ws-surface">
        <button
          type="button"
          className="w-full flex items-center gap-2 px-3 py-2.5 max-lg:min-h-[48px] max-lg:text-sm text-xs font-medium text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2 transition-colors"
          onClick={() => setNewFolderParentId(resolveNewFolderParent())}
        >
          <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-dashed border-ws-border-strong text-ws-ink-3">
            <Plus size={11} strokeWidth={2} />
          </span>
          {t('tree.view.newFolderPlaceholder')}
        </button>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

/**
 * W9-8 — walk up the parent_id chain from a starting project_id and return
 * the ROOT folder (no parent). Used to detect cross-client moves.
 */
function rootFor(
  projectId: string | null,
  byId: Map<string, ProjectNode>,
): ProjectNode | null {
  if (!projectId) return null;
  let cur: ProjectNode | undefined = byId.get(projectId);
  while (cur && cur.parent_id) {
    const parent: ProjectNode | undefined = byId.get(cur.parent_id);
    if (!parent) break;
    cur = parent;
  }
  return cur ?? null;
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <div aria-hidden className="px-3 py-2 space-y-2">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2"
          style={{ marginLeft: (i % 3) * 16 }}
        >
          <div className="w-5 h-5 rounded-full bg-ws-surface-2" style={{ opacity: 0.6 - i * 0.06 }} />
          <div className="flex-1 space-y-1">
            <div className="h-3 rounded-sm bg-ws-surface-2" style={{ opacity: 0.6 - i * 0.06, width: `${70 - i * 6}%` }} />
            <div className="h-2 rounded-sm bg-ws-surface-2" style={{ opacity: 0.4 - i * 0.04, width: `${40 - i * 4}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation('projects');
  return (
    <div className="px-4 py-8 text-center">
      <div className="mx-auto w-9 h-9 rounded-full bg-ws-accent-quiet text-ws-accent-ink grid place-items-center mb-3">
        <FolderPlus size={18} strokeWidth={1.75} />
      </div>
      <p className="text-sm font-medium text-ws-ink-2 mb-1">{t('tree.main.empty.title')}</p>
      <p className="text-xs text-ws-ink-3 leading-relaxed mb-3">
        {t('tree.main.empty.hint')}
      </p>
      <button
        type="button"
        className="ws-btn-primary inline-flex items-center gap-1.5 text-xs"
        onClick={onCreate}
      >
        <FolderPlus size={12} strokeWidth={2} />
        {t('tree.main.empty.cta')}
      </button>
    </div>
  );
}
